import React, { useEffect, useRef, useState } from "react";
import { Modal, Input, Button, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import NormalEditor from "../../components/NormalEditor";
import BlogDescriptionToolbar from "./BlogDescriptionToolbar";
import apiPath from "../../constants/apiPath";

// One "text" block, mirrors outlineBlockSchema on the backend
// (BlogWriteRequest.ts) — audienceValue / challenges / introduction /
// sourcesAndData / conclusionCta all use this same shape. The backend's
// `.image` field on each block still exists in the schema but this UI no
// longer writes it — images are now embedded inline inside the rich `text`/
// `content` HTML via NormalEditor's own image toolbar button, so a separate
// per-section image slot is redundant. Leaving `.image` null/unset here is
// harmless; do not resurrect it from this component.
const emptyBlock = () => ({ text: "" });
const emptyHeader = () => ({ title: "", content: "", subheaders: [] });
const emptySubheader = () => ({ title: "", content: "" });

// Fills in any missing pieces so the modal always has a fully-shaped draft
// to edit, whether `value` is an existing request's outline, undefined
// (brand-new request), or a partially-saved one from an older save.
//
// Starts a brand-new draft (no `value` at all yet) with ONE empty header
// already present, per direct user feedback — an empty "Body" section with
// only an "Add Header" button read as unfinished/broken. This default is
// only for the never-saved case: once a request has been saved (`value`
// exists), an explicitly empty `headers` array is left alone — the user may
// have deliberately removed every header, and re-injecting one on every
// re-open would make that impossible.
const normalizeOutline = (value) => {
  const headersSource = value?.headers?.length
    ? value.headers
    : value
    ? value.headers || []
    : [emptyHeader()];

  return {
    audienceValue: { ...emptyBlock(), ...(value?.audienceValue || {}) },
    challenges: { ...emptyBlock(), ...(value?.challenges || {}) },
    introduction: { ...emptyBlock(), ...(value?.introduction || {}) },
    headers: headersSource.map((h) => ({
      title: h?.title || "",
      content: h?.content || "",
      subheaders: (h?.subheaders || []).map((s) => ({
        title: s?.title || "",
        content: s?.content || "",
      })),
    })),
    sourcesAndData: { ...emptyBlock(), ...(value?.sourcesAndData || {}) },
    conclusionCta: { ...emptyBlock(), ...(value?.conclusionCta || {}) },
  };
};

// True once any section has real content — used by the parent form to show
// the "Blog description added" badge instead of the empty-state button.
// Strips HTML tags before checking so an editor left with only empty markup
// (e.g. "<p></p>") doesn't count as content.
// Exported so read-only display pages (BlogWriteRequestDetail.js) can use
// the exact same "does this rich-text field have real content" check —
// e.g. to avoid rendering an empty "<p></p>" block as if it were content.
export const hasText = (html) => !!html?.replace(/<[^>]*>/g, "").trim();

// NormalEditor's outline toolbar has no "insert link" button, so a writer
// typing a raw URL (e.g. into "List of Sources and Data") gets plain text,
// not a real <a>. Auto-linkify bare URLs at read-only display time instead
// of adding link-insertion UI. The negative lookbehind skips a URL already
// inside an href="..." (harmless future-proofing; nothing produces one
// today). Trailing punctuation that's clearly prose, not part of the URL
// (a period ending the sentence, a comma, a closing paren) is peeled off
// so the link doesn't swallow it.
export const linkifyHtml = (html) => {
  if (!html) return html;
  // The negative lookbehind only ever excluded href="..." — an <img
  // src="https://..."> (or any other url-valued attribute: data-*, poster,
  // etc.) still matched, so this wrapped the URL inside the attribute in a
  // literal <a href="...">...</a>, corrupting the tag's own src into
  // invalid markup ("Open in Editor" then either shows a broken image or,
  // worse, leaks the mangled tag as visible text). Excluding any URL
  // immediately preceded by `="` — i.e. already sitting inside SOME
  // attribute's value, not just href — covers src and every other case in
  // one guard.
  return html.replace(/(?<!=")(https?:\/\/[^\s<"]+)/g, (url) => {
    const trailingMatch = url.match(/[.,;:)\]]+$/);
    const trail = trailingMatch ? trailingMatch[0] : "";
    const clean = trail ? url.slice(0, -trail.length) : url;
    if (!clean) return url;
    return `<a href="${clean}" target="_blank" rel="noreferrer">${clean}</a>${trail}`;
  });
};
export const outlineHasContent = (outline) => {
  if (!outline) return false;
  return (
    hasText(outline.audienceValue?.text) ||
    hasText(outline.challenges?.text) ||
    hasText(outline.introduction?.text) ||
    hasText(outline.sourcesAndData?.text) ||
    hasText(outline.conclusionCta?.text) ||
    (outline.headers || []).length > 0
  );
};

// Wired to the same backend media-upload endpoint SingleImageUpload.js
// already proxies through (POST /admin/media/upload, auth via admin_token —
// see that component's own comment on why uploads go through the backend
// rather than straight to S3). react-draft-wysiwyg's image toolbar button
// requires exactly this (file) => Promise<{ data: { link: string } }> shape.
const uploadInlineImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${apiPath.baseURL}admin/media/upload`, formData, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return { data: { link: res.data?.data?.url } };
};

// Large, document-styled builder for the blog outline — deliberately reads
// like a document being drafted (Blog Title as a big heading, generous
// padding, serif-flavored section labels) rather than a cramped form, per
// direct user feedback on the old cramped TextArea+per-section-upload-button
// layout. Purely local state until Save commits the whole object back to the
// parent via onChange; Cancel just closes and throws the draft away.
function BlogDescriptionModal({ open, value, onChange, onClose, blogTitle }) {
  const [draft, setDraft] = useState(() => normalizeOutline(value));

  useEffect(() => {
    if (open) setDraft(normalizeOutline(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // --- Shared toolbar wiring ----------------------------------------------
  // One NormalEditor ref per outline-section instance, keyed the same way
  // the section itself is keyed (block key, or "header:<hi>" /
  // "sub:<hi>:<si>" for the dynamic Header/Subheader editors) so a shared
  // toolbar can drive whichever one is currently focused.
  //
  // IMPORTANT: NormalEditor's useImperativeHandle has no deps array, so it
  // replaces `forwardedRef.current` with a BRAND NEW object (fresh
  // toggleInlineStyle/insertImage closures) on every one of its own
  // re-renders — i.e. on every keystroke. An earlier version of this file
  // copied that pointer once, on focus (`activeEditorRef.current =
  // getEditorRef(key).current`), which meant it kept pointing at whatever
  // object happened to be live AT THAT INSTANT — stale after the very next
  // keystroke re-rendered the editor and replaced it. Clicking Bold after
  // typing then ran RichUtils.toggleInlineStyle against that stale,
  // pre-typing editorState, which is exactly what produced both reported
  // bugs: the freshly-typed text disappearing (state rewound to the old
  // snapshot) and characters typing in reverse (the stale closure also
  // carried a stale/collapsed selection, so each new keystroke inserted at
  // the wrong offset). Fix: only ever track the KEY of the focused editor;
  // resolve `editorRefsMap.current[key].current` fresh at click time
  // instead, so it's always the latest object.
  const editorRefsMap = useRef({});
  const getEditorRef = (key) => {
    if (!editorRefsMap.current[key]) editorRefsMap.current[key] = React.createRef();
    return editorRefsMap.current[key];
  };
  const activeKeyRef = useRef(null);
  const [activeKey, setActiveKey] = useState(null);
  const [activeStyles, setActiveStyles] = useState([]);
  const [activeBlockType, setActiveBlockType] = useState(null);

  const editorHandlersFor = (key) => ({
    ref: getEditorRef(key),
    onFocus: () => {
      activeKeyRef.current = key;
      setActiveKey(key);
    },
    onActiveStylesChange: (styles) => {
      if (activeKey === key) setActiveStyles(styles);
    },
    onActiveBlockTypeChange: (blockType) => {
      if (activeKey === key) setActiveBlockType(blockType);
    },
  });

  const updateBlock = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const addHeader = () => setDraft((d) => ({ ...d, headers: [...d.headers, emptyHeader()] }));
  const removeHeader = (hi) => setDraft((d) => ({ ...d, headers: d.headers.filter((_, i) => i !== hi) }));
  const updateHeader = (hi, patch) =>
    setDraft((d) => ({ ...d, headers: d.headers.map((h, i) => (i === hi ? { ...h, ...patch } : h)) }));

  const addSubheader = (hi) =>
    setDraft((d) => ({
      ...d,
      headers: d.headers.map((h, i) => (i === hi ? { ...h, subheaders: [...h.subheaders, emptySubheader()] } : h)),
    }));
  const removeSubheader = (hi, si) =>
    setDraft((d) => ({
      ...d,
      headers: d.headers.map((h, i) =>
        i === hi ? { ...h, subheaders: h.subheaders.filter((_, j) => j !== si) } : h
      ),
    }));
  const updateSubheader = (hi, si, patch) =>
    setDraft((d) => ({
      ...d,
      headers: d.headers.map((h, i) =>
        i === hi
          ? { ...h, subheaders: h.subheaders.map((s, j) => (j === si ? { ...s, ...patch } : s)) }
          : h
      ),
    }));

  const handleSave = () => {
    onChange(draft);
    onClose();
  };

  const renderBlock = (label, key, minHeight = 140) => (
    <div className="blog-desc-section">
      <div className="blog-desc-section-label">{label}</div>
      <NormalEditor
        toolbarPreset="outline"
        hideToolbar
        content={draft[key].text}
        onChange={(html) => updateBlock(key, { text: html })}
        minHeight={minHeight}
        placeholder={`Write ${label.toLowerCase()}...`}
        imageUploadCallback={uploadInlineImage}
        {...editorHandlersFor(key)}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      title="Blog Post Outline Framework"
      width={960}
      style={{ top: 20 }}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      cancelText="Cancel"
      bodyStyle={{ maxHeight: "82vh", overflowY: "auto", padding: 0 }}
      className="blog-desc-modal"
    >
      <BlogDescriptionToolbar
        editorRefsMap={editorRefsMap}
        activeKeyRef={activeKeyRef}
        activeStyles={activeStyles}
        activeBlockType={activeBlockType}
        onUploadImage={uploadInlineImage}
      />
      <div className="blog-desc-page">
        {blogTitle && (
          <>
            <div className="blog-desc-page-eyebrow">Blog Title</div>
            <h1 className="blog-desc-page-title">{blogTitle}</h1>
            <Divider className="blog-desc-page-divider" />
          </>
        )}

        {renderBlock("What's In It For the Audience", "audienceValue")}
        {renderBlock("Challenges", "challenges")}
        {renderBlock("Introduction", "introduction")}

        <Divider className="blog-desc-page-divider" />

        <div className="blog-desc-section">
          <div className="blog-desc-section-label">Body</div>
          {draft.headers.map((header, hi) => (
            <div key={hi} className="blog-desc-header-block">
              <div className="blog-desc-header-row">
                <Input
                  placeholder="Header title"
                  value={header.title}
                  onChange={(e) => updateHeader(hi, { title: e.target.value })}
                  bordered={false}
                  className="blog-desc-header-title-input"
                />
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  title="Remove header"
                  onClick={() => removeHeader(hi)}
                />
              </div>
              <NormalEditor
                toolbarPreset="outline"
                hideToolbar
                content={header.content}
                onChange={(html) => updateHeader(hi, { content: html })}
                minHeight={120}
                placeholder="Header content"
                imageUploadCallback={uploadInlineImage}
                {...editorHandlersFor(`header:${hi}`)}
              />

              {header.subheaders.map((sub, si) => (
                <div key={si} className="blog-desc-subheader-block">
                  <div className="blog-desc-header-row">
                    <Input
                      placeholder="Subheader title"
                      value={sub.title}
                      onChange={(e) => updateSubheader(hi, si, { title: e.target.value })}
                      bordered={false}
                      className="blog-desc-subheader-title-input"
                    />
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      title="Remove subheader"
                      onClick={() => removeSubheader(hi, si)}
                    />
                  </div>
                  <NormalEditor
                    toolbarPreset="outline"
                    hideToolbar
                    content={sub.content}
                    onChange={(html) => updateSubheader(hi, si, { content: html })}
                    minHeight={100}
                    placeholder="Subheader content"
                    imageUploadCallback={uploadInlineImage}
                    {...editorHandlersFor(`sub:${hi}:${si}`)}
                  />
                </div>
              ))}

              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => addSubheader(hi)}
                style={{ marginTop: 8 }}
              >
                Add Subheader
              </Button>
            </div>
          ))}

          <Button type="dashed" icon={<PlusOutlined />} onClick={addHeader} style={{ marginTop: 12 }}>
            Add Header
          </Button>
        </div>

        <Divider className="blog-desc-page-divider" />

        {renderBlock("List of Sources and Data", "sourcesAndData")}
        {renderBlock("Conclusion / Call to Action", "conclusionCta")}
      </div>
    </Modal>
  );
}

export default BlogDescriptionModal;
